import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { Button, Divider, Typography } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/system";
import clsx from "clsx";
import { AvatarInfo, InfiniteScroll, SearchBar } from "components";
import { KeyConstant, LangConstant } from "const";
import { useAccountList, useContactList } from "hooks";
import { isFunction } from "lodash";
import PropTypes from "prop-types";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BranchSelectors } from "redux-store";
import { getInteractor } from "services/local.service";
import { StorageUtil } from "utils";
import { getNSLang } from "utils/lang.utils";
import ACSelectContact from "./ACSelectContact";

const SelectingListContact = ({ groupMemberIds, onComplete, maximumLimit, minimumLimit }) => {
  const classes = useStyles();
  const { t: getLabel } = useTranslation(LangConstant.NS_HOME);
  const chatObjContent = getLabel(LangConstant.OBJ_ADDING_CHAT, { returnObjects: true });
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const inputRef = useRef();
  const handleSelectRef = useRef({});
  const selectedContactRef = useRef([]);

  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);

  const isBranchServer = useSelector(BranchSelectors.isBranchServer);

  const [viewingList, setViewingList] = useState([]);
  const [selectedContact, setSelectedContact] = useState([]);
  const [totalAccountItems, setTotalAccountItems] = useState(0);

  const { contactList, isEnd: isEndContactList, getAllContactByName } = useContactList();
  const {
    accountList,
    queryPagination: { page: queryPage, totalPage },
    getAccountList,
  } = useAccountList();

  const onCreateSelected = useCallback(
    item => () => {
      const currentSelectedContact = selectedContactRef.current;
      const newContactIndex = currentSelectedContact.findIndex(contact => contact.identifyId === item.identifyId);
      const newContactList =
        newContactIndex >= 0
          ? currentSelectedContact.filter(contact => contact.identifyId !== item.identifyId)
          : [...currentSelectedContact, item];

      setSelectedContact(newContactList);
      selectedContactRef.current = newContactList;
    },
    [],
  );

  const onDetectItem = item =>
    selectedContactRef.current.findIndex(contact => contact.identifyId === item.identifyId) >= 0;

  const onCompleteSelectingContact = () => isFunction(onComplete) && onComplete(selectedContact);

  const onChangeSearchInput = content => {
    inputRef.current = content;
    if (isBranchServer && content) {
      getAccountList({
        keyword: content,
        page: 1,
        isHiddenBot: true,
        exceptionIds: (groupMemberIds || []).concat([accountId]),
      });
    } else {
      getAllContactByName(content);
    }
  };

  const onDelete = useCallback(item => {
    const newSelectedContact = selectedContactRef.current.filter(contact => contact.identifyId !== item.identifyId);
    setSelectedContact(newSelectedContact);
    selectedContactRef.current = newSelectedContact;
  }, []);

  const handleLoadMore = () => {
    if (isBranchServer && inputRef.current && queryPage <= totalPage) {
      getAccountList({
        keyword: inputRef.current,
        page: queryPage + 1,
        isHiddenBot: true,
        exceptionIds: (groupMemberIds || []).concat([accountId]),
      });
    } else if (!isEndContactList) {
      getAllContactByName(inputRef.current);
    }
  };

  const handleInitData = async () => {
    const contactList = await getAllContactByName();

    if (isBranchServer) {
      const totalAccount = await getInteractor(prefixKey).LocalAccountService.countAccountInBranch({
        keyword: "",
        isHiddenBot: true,
        exceptionIds: (groupMemberIds || []).concat([accountId]),
      });
      setTotalAccountItems(totalAccount);
    } else {
      const contactLength = contactList.length - (groupMemberIds?.length || 0);
      setTotalAccountItems(contactLength >= 0 ? contactLength : 0);
    }
  };

  useEffect(() => {
    handleInitData();
  }, []);

  useEffect(() => {
    let viewList = [];
    if (isBranchServer && inputRef.current) {
      viewList = accountList.filter(
        item => !(item.identifyId === accountId || item.isBot || groupMemberIds?.includes(item.identifyId)),
      );
    } else {
      viewList = contactList.filter(
        item => !(item.identifyId === accountId || item.isBot || groupMemberIds?.includes(item.identifyId)),
      );
    }

    viewList.forEach(item => {
      if (!handleSelectRef.current[item.identifyId]) handleSelectRef.current[item.identifyId] = onCreateSelected(item);
    });

    setViewingList(viewList);
  }, [accountList, contactList]);

  const isShowMaximumError = selectedContact.length >= maximumLimit;
  const isDisabledButton = selectedContact.length < minimumLimit || isShowMaximumError;

  return (
    <Box className={classes.rootContainer}>
      <Box className={classes.rootSpace}>
        <SearchBar onChange={onChangeSearchInput} />
      </Box>
      {isShowMaximumError && (
        <Typography className={clsx(classes.errorNoti)}>
          {getNSLang(LangConstant.NS_HOME, "FM_CONFERENCE_MEMBER_ERROR", { max_person: maximumLimit })}
        </Typography>
      )}

      <Typography variant="subtitle2" className={clsx(classes.subTitle, classes.rootSpace)}>
        {getLabel(LangConstant.FM_CHOOSE_MEMBER, { number: selectedContact.length })}
      </Typography>
      {selectedContact.length > 0 ? (
        <Box className={clsx(classes.selectedList)}>
          {selectedContact.map(item => (
            <ACSelectContact key={item.id} contact={item} onDelete={onDelete} />
          ))}
        </Box>
      ) : (
        <Box className={classes.noSelectedList}>
          <PeopleAltIcon className={classes.noChooseMemberIcon} color="disabled" fontSize="large" />
          <Typography>{getLabel(LangConstant.TXT_NO_MEMBER_CHOOSE)}</Typography>
        </Box>
      )}

      <Typography variant="subtitle2" className={clsx(classes.subTitle, classes.rootSpace)}>
        {getLabel(LangConstant.FM_MEMBER, { number: totalAccountItems })}
      </Typography>
      <InfiniteScroll className={classes.infiniteScroll} onScrollToBottom={handleLoadMore}>
        {viewingList.map((item, index) => {
          return (
            <Box key={`${item.avatarId}-${index}`}>
              {index > 0 && <Divider variant="inset" />}
              <ContactItem
                data={item}
                onClick={handleSelectRef.current[item.identifyId]}
                checked={onDetectItem(item)}
              />
            </Box>
          );
        })}
      </InfiniteScroll>
      <Box className={classes.btnContainer}>
        <Button
          variant="contained"
          onClick={onCompleteSelectingContact}
          fullWidth
          className={classes.btnDone}
          size="large"
          classes={{ disabled: classes.disabledButton }}
          disabled={isDisabledButton}
        >
          {chatObjContent.btnDone}
        </Button>
      </Box>
    </Box>
  );
};

SelectingListContact.propTypes = {
  maximumLimit: PropTypes.number,
  minimumLimit: PropTypes.number,

  onComplete: PropTypes.func,
};

SelectingListContact.defaultProps = {
  maximumLimit: 1000,
  minimumLimit: 1,
};

export default SelectingListContact;

const ContactItem = memo(({ data, onClick, checked }) => {
  const avatarObj = { avatarId: data.avatarId, name: data.contactName || data.name || data.phone };

  return (
    <AvatarInfo
      avatar={avatarObj}
      title={data.contactName || data.name || data.phone}
      onClick={onClick}
      button={true}
      endComponent={<Checkbox color="primary" checked={checked} />}
    />
  );
});

const useStyles = makeStyles(theme => ({
  rootContainer: {
    height: "100%",
  },

  rootSpace: {
    paddingLeft: 16,
    paddingRight: 16,
  },

  subTitle: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textAlign: "left",
    marginTop: 10,
  },

  selectedList: {
    display: "flex",
    height: 80,
    marginLeft: 16,
    marginRight: 16,
    overflow: "auto",
  },

  btnContainer: {
    position: "sticky",
    padding: "10px 16px",
    bottom: 0,
    marginTop: "auto",
    backgroundColor: "white",
    paddingBottom: 15,
  },

  btnDone: {
    cursor: "pointer",
  },

  disabledButton: {
    backgroundColor: "#72B9E6 !important",
    color: theme.palette.white + "!important",
  },

  errorNoti: {
    color: "red",
    fontSize: "0.75rem",
    textAlign: "center",
  },

  noSelectedList: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: 80,
    justifyContent: "center",
  },

  infiniteScroll: {
    height: "calc(100% - 250px)",
    overflow: "auto",
    padding: "16px 14px",
  },
}));
